import React from 'react';
import Modal from '../common/ModalPopup';
import * as HELPER from "../config/helper";

const roundAmount = (value: any) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
};

const normalizeTrenchType = (value: any) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, "-");

const getResultMessage = (item: any, type: "success" | "error") => {
    if (typeof item === "string") {
        return item;
    }

    if (type === "success") {
        return "Payout completed successfully.";
    }

    if (item?.message) {
        return item.message;
    }

    return item?.message || "No details available.";
};

const BulkPayoutResultPopup: React.FC<any> = ({ show, handleClose, result, remittanceList = [] }) => {
    const summary = result?.data?.summary || {};
    const successes = Array.isArray(result?.data?.successes) ? result.data.successes : [];
    const errors = Array.isArray(result?.data?.errors) ? result.data.errors : [];

    const getMatchedRemittance = (item: any) => {
        const leadId = item?.lead_id;
        const trenchType = normalizeTrenchType(item?.trench_type || item?.remittance_type_name || item?.remittance_type);

        return remittanceList.find((row: any) => {
            const rowLeadId = row?.leadId ?? row?.lead_id;
            const rowTrenchType = normalizeTrenchType(row?.remittance_type_slug || row?.remittance_type_name || row?.trench_type || row?.remittance_type);
            return String(rowLeadId) === String(leadId) && rowTrenchType === trenchType;
        });
    };

    const getPayoutAmount = (item: any) => {
        const row = getMatchedRemittance(item);
        if (!row) {
            return null;
        }

        const trenchType = normalizeTrenchType(item?.trench_type || item?.remittance_type_name || item?.remittance_type);
        const disbursementFees = Number(row?.disbursement_fees || 0);

        const calculationMap: Record<string, number> = {
            "partner": Number(row?.gross_written_premium || 0) + Number(row?.admin_fee || 0) + disbursementFees,
            "agency-points": Number(row?.net_point_disbursal || 0) + disbursementFees,
            "agency-commission": Number(row?.net_commission_disbursal || 0) + disbursementFees,
        };

        const total = roundAmount(calculationMap[trenchType] ?? row?.amount_to_disbursed ?? 0);
        return HELPER.addCurrencyFormatting(total);
    };

    const renderResultItem = (item: any, index: number, type: "success" | "error") => {
        const leadId = item?.lead_id;
        const trenchType = item?.trench_type || item?.remittance_type_name || item?.remittance_type;
        const payoutAmount = type === "success" ? getPayoutAmount(item) : null;

        return (
            <li key={`${type}-${index}`} className={`bulk-payout-result-popup__item bulk-payout-result-popup__item--${type}`}>
                {(leadId || trenchType) && (
                    <div className="bulk-payout-result-popup__item-meta">
                        {leadId && <span className="bulk-payout-result-popup__tag">Lead ID: {leadId}</span>}
                        {trenchType && <span className="bulk-payout-result-popup__tag">{trenchType}</span>}
                        {payoutAmount && <span className="bulk-payout-result-popup__tag">Payout: {payoutAmount}</span>}
                    </div>
                )}
                <p className="bulk-payout-result-popup__item-message">{getResultMessage(item, type)}</p>
            </li>
        );
    };

    return (
        <div className="bulk-payout-result-popup view-timeline-popup image-timeline-popup">
            <Modal show={show} handleClose={handleClose} hideClose={true} from={"RemittanceList"}>
                <div className="modal-header bulk-payout-result-popup__header">
                    <div className="bulk-payout-result-popup__status">Bulk Payout Summary</div>
                    <h2>{result?.message || "Bulk payout result"}</h2>
                    <p className="bulk-payout-result-popup__subtitle">
                        Please Review the payout summary below -
                    </p>
                </div>
                <div className="modal-body bulk-payout-result-popup__body">
                    <div className="bulk-payout-result-popup__summary">
                        <div className="bulk-payout-result-popup__summary-card">
                            <span className="bulk-payout-result-popup__summary-label">Total</span>
                            <strong className="bulk-payout-result-popup__summary-value">{summary?.total || 0}</strong>
                        </div>
                        <div className="bulk-payout-result-popup__summary-card bulk-payout-result-popup__summary-card--success">
                            <span className="bulk-payout-result-popup__summary-label">Success</span>
                            <strong className="bulk-payout-result-popup__summary-value">{summary?.success_count || 0}</strong>
                        </div>
                        <div className="bulk-payout-result-popup__summary-card bulk-payout-result-popup__summary-card--error">
                            <span className="bulk-payout-result-popup__summary-label">Error</span>
                            <strong className="bulk-payout-result-popup__summary-value">{summary?.error_count || 0}</strong>
                        </div>
                    </div>

                    {successes.length > 0 && (
                        <div className="bulk-payout-result-popup__section">
                            <div className="bulk-payout-result-popup__section-header">
                                <h3>Successful Payouts</h3>
                                <span className="bulk-payout-result-popup__count bulk-payout-result-popup__count--success">{successes.length}</span>
                            </div>
                            <div className="bulk-payout-result-popup__section-body bulk-payout-result-popup__section-body--compact">
                                <ul className="bulk-payout-result-popup__list">
                                    {successes.map((item: any, index: number) => renderResultItem(item, index, "success"))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {errors.length > 0 && (
                        <div className="bulk-payout-result-popup__section bulk-payout-result-popup__section--error">
                            <div className="bulk-payout-result-popup__section-header">
                                <h3>Failed Payouts</h3>
                                <span className="bulk-payout-result-popup__count bulk-payout-result-popup__count--error">{errors.length}</span>
                            </div>
                            <div className="bulk-payout-result-popup__section-body bulk-payout-result-popup__section-body--errors">
                                <ul className="bulk-payout-result-popup__list">
                                    {errors.map((item: any, index: number) => renderResultItem(item, index, "error"))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {successes.length === 0 && errors.length === 0 && (
                        <div className="bulk-payout-result-popup__empty-state">
                            <p>{result?.message || "No payout details available."}</p>
                        </div>
                    )}

                    <div className="bulk-payout-result-popup__actions">
                        <button className="btn-primary" onClick={handleClose}>OK</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default BulkPayoutResultPopup;
